import { doc, getDoc, updateDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import { calculateRankProgression } from "./calculateRankProgression";
import { calculatePointMultiplier } from "./pointMultiplier";
import { updateUserStreak } from "./updateUserStreak";
import { checkAndUpdateAchievements } from "./achievements";
import { recordUserEvent, USER_EVENT_TYPES } from "./userEvents";

export const updatePointsOnConversation = async (userId) => {
  const userStatsRef = doc(db, "userStats", userId);

  try {
    // First update streak and get any streak-related data
    const streakData = await updateUserStreak(userId);
    
    const userStatsDoc = await getDoc(userStatsRef);
    if (!userStatsDoc.exists()) return;
    
    const data = userStatsDoc.data();

    // Calculate multiplier
    const { multiplier, isNightOwl, isWeekend, isPeakHours } = calculatePointMultiplier();

    // Calculate points to add (base 10 points for conversations)
    const basePoints = 10;
    const pointsForAction = Math.round(basePoints * multiplier);
    const newTotalPoints = (data.points.total || 0) + pointsForAction;
    const newDailyPoints = (data.points.daily || 0) + pointsForAction;

    // Create update payload
    const updatePayload = {
      "points.total": newTotalPoints,
      "points.daily": newDailyPoints,
      // Increment conversation count
      "achievements.conversationsStarted": increment(1)
    };

    // Add multiplier tracking
    if (isNightOwl) updatePayload["multipliers.nightowl"] = increment(1);
    if (isWeekend) updatePayload["multipliers.weekend"] = increment(1);
    if (isPeakHours) updatePayload["multipliers.peakHours"] = increment(1);

    // Update Firestore document
    await updateDoc(userStatsRef, updatePayload);

    // Recalculate rank progression
    const rankUpdateResult = await calculateRankProgression(userId);
    
    // Get updated user data after the update
    const updatedUserDoc = await getDoc(userStatsRef);
    const updatedData = updatedUserDoc.data();

    // Check for conversation-related achievements
    const conversationsCount = updatedData.achievements.conversationsStarted || 0;
    const achievementResults = await checkAndUpdateAchievements(
      userId, 
      'CONVERSATION_MILESTONES', 
      conversationsCount
    );
    
    // Record events
    const eventPromises = [];
    
    // Record points earned event
    eventPromises.push(
      recordUserEvent(userId, USER_EVENT_TYPES.POINTS_EARNED, {
        amount: pointsForAction,
        reason: 'new_conversation',
        multiplier
      })
    );
    
    // Record achievement events if any were unlocked
    if (achievementResults && achievementResults.newAchievements.length > 0) {
      eventPromises.push(
        recordUserEvent(userId, USER_EVENT_TYPES.ACHIEVEMENT_UNLOCKED, {
          achievements: achievementResults.newAchievements,
          bonusPoints: achievementResults.bonusPoints
        })
      );
    }
    
    // Record streak milestone event if applicable
    if (streakData && streakData.milestoneReached) {
      eventPromises.push(
        recordUserEvent(userId, USER_EVENT_TYPES.STREAK_MILESTONE, {
          streak: streakData.streak,
          bonusPoints: streakData.bonusPoints
        })
      );
    }
    
    // Wait for all event recordings to complete
    await Promise.all(eventPromises);

    console.log(
      `Conversation points updated! Added ${pointsForAction} points (${multiplier}x multiplier).` +
      (achievementResults ? ` Unlocked ${achievementResults.newAchievements.length} achievements!` : '') +
      (rankUpdateResult?.isRankUp ? ` 🏆 Ranked up to ${rankUpdateResult.currentRank}!` : '')
    );
    
    return {
      pointsAdded: pointsForAction,
      multiplier,
      achievements: achievementResults?.newAchievements || [],
      streakData,
      rankUpdate: rankUpdateResult
    };
  } catch (error) {
    console.error("Error updating points on conversation:", error);
  }
};



// FUTURE REWARD SYSTEM
// import { doc, getDoc, updateDoc, increment } from "firebase/firestore";
// import { db } from "../firebase";
// import { calculateRankProgression } from "./calculateRankProgression";
// import { calculatePointMultiplier } from "./pointMultiplier";
// import { updateUserStreak } from "./updateUserStreak";
// import { checkAndUpdateAchievements } from "./achievements";
// import { recordUserEvent, USER_EVENT_TYPES } from "./userEvents";
// import { enhanceUpdatePointsOnConversation } from '../userservices/rewardHelpers';

// const originalUpdatePointsOnConversation = async (userId) => {
//   // ALL YOUR EXISTING CODE STAYS EXACTLY THE SAME
//   const userStatsRef = doc(db, "userStats", userId);

//   try {
//     const streakData = await updateUserStreak(userId);
//     const userStatsDoc = await getDoc(userStatsRef);
//     if (!userStatsDoc.exists()) return;
    
//     const data = userStatsDoc.data();
//     const { multiplier, isNightOwl, isWeekend, isPeakHours } = calculatePointMultiplier();
//     const basePoints = 10;
//     const pointsForAction = Math.round(basePoints * multiplier);
//     const newTotalPoints = (data.points.total || 0) + pointsForAction;
//     const newDailyPoints = (data.points.daily || 0) + pointsForAction;

//     const updatePayload = {
//       "points.total": newTotalPoints,
//       "points.daily": newDailyPoints,
//       "achievements.conversationsStarted": increment(1)
//     };

//     if (isNightOwl) updatePayload["multipliers.nightowl"] = increment(1);
//     if (isWeekend) updatePayload["multipliers.weekend"] = increment(1);
//     if (isPeakHours) updatePayload["multipliers.peakHours"] = increment(1);

//     await updateDoc(userStatsRef, updatePayload);
//     const rankUpdateResult = await calculateRankProgression(userId);
    
//     const updatedUserDoc = await getDoc(userStatsRef);
//     const updatedData = updatedUserDoc.data();

//     const conversationsCount = updatedData.achievements.conversationsStarted || 0;
//     const achievementResults = await checkAndUpdateAchievements(
//       userId, 
//       'CONVERSATION_MILESTONES', 
//       conversationsCount
//     );
    
//     const eventPromises = [];
    
//     eventPromises.push(
//       recordUserEvent(userId, USER_EVENT_TYPES.POINTS_EARNED, {
//         amount: pointsForAction,
//         reason: 'new_conversation',
//         multiplier
//       })
//     );
    
//     if (achievementResults && achievementResults.newAchievements.length > 0) {
//       eventPromises.push(
//         recordUserEvent(userId, USER_EVENT_TYPES.ACHIEVEMENT_UNLOCKED, {
//           achievements: achievementResults.newAchievements,
//           bonusPoints: achievementResults.bonusPoints
//         })
//       );
//     }
    
//     if (streakData && streakData.milestoneReached) {
//       eventPromises.push(
//         recordUserEvent(userId, USER_EVENT_TYPES.STREAK_MILESTONE, {
//           streak: streakData.streak,
//           bonusPoints: streakData.bonusPoints
//         })
//       );
//     }
    
//     await Promise.all(eventPromises);

//     console.log(
//       `Conversation points updated! Added ${pointsForAction} points (${multiplier}x multiplier).` +
//       (achievementResults ? ` Unlocked ${achievementResults.newAchievements.length} achievements!` : '') +
//       (rankUpdateResult?.isRankUp ? ` 🏆 Ranked up to ${rankUpdateResult.currentRank}!` : '')
//     );
    
//     return {
//       pointsAdded: pointsForAction,
//       multiplier,
//       achievements: achievementResults?.newAchievements || [],
//       streakData,
//       rankUpdate: rankUpdateResult
//     };
//   } catch (error) {
//     console.error("Error updating points on conversation:", error);
//   }
// };

// // NEW: Enhanced export with casino rewards
// export const updatePointsOnConversation = async (userId) => {
//   return await enhanceUpdatePointsOnConversation(originalUpdatePointsOnConversation, userId);
// };